import React, { useState, useEffect } from "react";
import { Fingerprint, Loader2, Plus, Trash2, Wand2, Power } from "lucide-react";
import { getStyle, createStyle, toggleStyle, deleteStyle } from "../../lib/api";
import { SectionHeader } from "./Sections";
import Markdown from "./Markdown";
import { toast } from "sonner";
import SignatureVoice from "./SignatureVoice";

const CONTEXTS = [
  "Professional email", "WhatsApp message", "Executive note", "Article / Op-ed",
  "Economic commentary", "HR & leadership", "Social media (LinkedIn)", "Speech",
  "Personal message", "Creative writing", "Hindi", "Marathi",
];

export default function StyleClone() {
  const [profiles, setProfiles] = useState([]);
  const [label, setLabel] = useState("Professional email");
  const [samples, setSamples] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = () => getStyle().then(setProfiles).catch(() => {});
  useEffect(() => { load(); }, []);

  const learn = async () => {
    if (samples.trim().length < 40) {
      toast.error("Paste at least a few sentences of your writing.");
      return;
    }
    setBusy(true);
    try {
      await createStyle({ label, context_key: label.toLowerCase(), samples });
      setSamples("");
      toast.success(`Aria learned your "${label}" voice`);
      await load();
    } catch (e) {
      toast.error("Could not analyze the samples. Try again.");
    }
    setBusy(false);
  };

  const flip = async (id) => { await toggleStyle(id); load(); };
  const remove = async (id) => { await deleteStyle(id); load(); };

  return (
    <div>
      <SectionHeader overline="Aria · Your Voice Clone" title="Write & Speak Like You"
        desc="Set how Aria sounds, then teach her how you write. Every draft and spoken reply will feel unmistakably yours." />

      <SignatureVoice />

      <div className="glass-card p-5 mb-6 fade-up">
        <div className="overline mb-3 flex items-center gap-1.5"><Fingerprint size={13} /> Teach Aria a new voice</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {CONTEXTS.map((c) => (
            <button key={c} onClick={() => setLabel(c)}
              className={`text-[11px] px-2.5 py-1 rounded-full transition ${label === c ? "gold-btn" : "border border-white/10 text-slate-400 hover:border-amber-500/40"}`}
              data-testid={`style-context-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>
        <textarea value={samples} onChange={(e) => setSamples(e.target.value)} rows={6}
          placeholder={`Paste 2-3 real examples of your "${label}" writing here. The more authentic, the better the clone.`}
          className="w-full bg-[#0F131C] border border-white/10 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-amber-500/50"
          data-testid="style-samples" />
        <div className="flex justify-end mt-3">
          <button onClick={learn} disabled={busy} className="gold-btn flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm disabled:opacity-40" data-testid="style-learn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Learn my style
          </button>
        </div>
      </div>

      {profiles.length === 0 ? (
        <div className="glass-card p-12 text-center text-slate-500">
          <Fingerprint size={30} className="mx-auto mb-3 opacity-50" />
          No voice profiles yet. Teach Aria your first one above.
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map((p) => (
            <div key={p.id} className="glass-card p-5 fade-up" data-testid="style-profile">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: p.active ? "rgba(226,184,87,0.2)" : "rgba(255,255,255,0.05)", color: p.active ? "#E2B857" : "#64748B" }}>
                    <Fingerprint size={15} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-sm">{p.label}</div>
                    <div className={`text-[11px] ${p.active ? "text-emerald-400" : "text-slate-500"}`}>{p.active ? "Active · used in all drafts" : "Inactive"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => flip(p.id)} className={`p-2 rounded-lg transition ${p.active ? "text-amber-400 hover:bg-amber-500/15" : "text-slate-500 hover:bg-white/10"}`} title="Toggle active" data-testid="style-toggle">
                    <Power size={15} />
                  </button>
                  <button onClick={() => remove(p.id)} className="p-2 rounded-lg text-slate-500 hover:bg-rose-500/15 hover:text-rose-400 transition" data-testid="style-delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <button onClick={() => setOpen(open === p.id ? null : p.id)} className="text-[11px] text-amber-400/80 hover:text-amber-300 mt-3" data-testid="style-view-guide">
                {open === p.id ? "Hide" : "View"} extracted style fingerprint
              </button>
              {open === p.id && (
                <div className="mt-2 border-t border-white/5 pt-3 max-h-72 overflow-y-auto">
                  <Markdown text={p.style_guide} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
